'use client';

import { ReadonlyURLSearchParams } from 'next/navigation';

import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';

import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import {
    getGRSFilterBy,
    getSearchResultsType,
} from '@/services/search/services/Grs';
import { isWAPOEnabledForWarehouse } from '@/services/search/services/LucidWorks';
import { SearchResult, WarehouseCookieValue } from '@/services/search/types';
import { SearchServiceProps } from '@/services/search/types';
import {
    getLocationsFromCookie,
    getUserLocationFromDeliveryLocations,
} from '@/services/search/utils';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { SearchResultGRS } from '@/src/services/search/services/GRS.types';
import type { GRSFacetConfigProperties } from '@/src/types/contentStack';
import {
    LocWHCookies,
    SearchPageDigitalDataLayerProps,
} from '@/src/types/pages/search';
import { getLocalWarehouseFromCookies } from '@/src/utils/cookieUtils';
import { SearchResultLW } from '@/types/searchQuery';
import {
    type ContentStackEntryDataProps,
    useAuth,
    useBrowseContext,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import {
    populateCommonSearchDigitalData,
    populateFusionDigitalData,
} from './lib';

/**
 * Function to stringify the keyword if it is an array
 * @param keyword
 * @returns
 */
export function stringifyKeyword(keyword: string | string[] | undefined) {
    return Array.isArray(keyword) ? keyword[0] : keyword;
}
/**
 * Function to populate fusion props for analytics
 * @param searchParams
 * @param searchResult
 * @returns
 */
export function populateFusionAnalyticProps(
    searchParams?: ReadonlyURLSearchParams,
    searchResult?: SearchResult,
    searchService?: SearchServiceProps,
    sortType?: string,
    grsFacetConfig?: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    whloc?: string,
    categoryTitle?: string,
    mdo?: string,
    userLocation?: string,
    isWAPOEnabledWarehouse: boolean = false,
) {
    const experimentIds = [searchService || 'def'];
    const searchProvider = searchService?.toUpperCase() || 'DEF';
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        const searchResultGRS = searchResult as SearchResultGRS;
        const filter = getGRSFilterBy({
            searchParams,
            whloc,
            grsFacetConfig,
            categoryPageId: categoryTitle,
            mdoLocationId: mdo,
            isWAPOEnabledWarehouse,
            userLocation,
        });

        const keywordParam = stringifyKeyword(
            searchParams?.get('keyword') || '',
        );

        return {
            actualSearchTerm: keywordParam,
            attributionToken: searchResultGRS?.searchResult?.attributionToken,
            experimentIds,
            filter,
            orderBy: sortType,
            searchProvider,
            searchResultsType: getSearchResultsType(searchResultGRS),
            searchTerm:
                searchResultGRS?.searchResult?.correctedQuery || keywordParam,
        };
    } else {
        const searchResultLW = searchResult as SearchResultLW;

        return {
            actualSearchTerm: searchResultLW?.metrics?.original
                ? searchResultLW?.metrics?.original
                : stringifyKeyword(searchParams?.get('keyword') || ''),
            searchTerm: searchResultLW?.metrics?.correction
                ? searchResultLW?.metrics?.correction
                : stringifyKeyword(searchParams?.get('keyword') || ''),
            filter: [searchParams?.get('refine')],
            fusionQueryId: searchResultLW?.metrics?.queryId,
            orderBy: sortType,
            searchProvider,
            xFusionExitCode: searchResultLW?.metrics?.xFusionExitCode,
            experimentIds,
        };
    }
}

const SearchPageDigitalDataLayer: React.FC<SearchPageDigitalDataLayerProps> = ({
    lang,
    searchProps,
    pageCrumbs,
    pageName,
    productData,
    site,
}) => {
    const { isUserSignedIn, isLoading } = useAuth();
    const [cookies] = useCookies();

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const initializeDigitalData = async () => {
            const isBD = process.env.NEXT_PUBLIC_SITE?.endsWith('BD');

            if (site && lang) {
                const isSignedIn = await isUserSignedIn();
                const sessionStatus = isSignedIn
                    ? 'Authenticated'
                    : 'Unauthenticated';

                populateCommonSearchDigitalData(
                    lang,
                    getLocalWarehouseFromCookies(cookies, {
                        isBD,
                    }) as LocWHCookies,
                    sessionStatus,
                    site,
                    pageName,
                    pageCrumbs,
                );
            } else if (searchProps) {
                populateFusionDigitalData(searchProps, pageName);
            } else if (productData) {
                const productExistsInList = window.digitalData?.products?.some(
                    item =>
                        item.pid === productData.pid &&
                        productData.searchDisplayRank ===
                            item.searchDisplayRank,
                );

                if (!productExistsInList) {
                    window?.digitalData?.products?.push(productData);
                }
            }
        };

        void initializeDigitalData().catch(error => {
            console.error(
                'Failed to initialize page-level digital data.',
                error,
            );
        });
    }, [
        site,
        lang,
        searchProps,
        productData,
        cookies,
        pageCrumbs,
        pageName,
        isUserSignedIn,
        isLoading,
    ]);
    return null;
};

export const SearchPageDigitalDataLayerWithSearchResults = ({
    grsFacetConfig,
    categoryTitle,
    lang,
    site,
    isWAPOEnabled,
    wapoEnabledWarehouses,
    ...rest
}: SearchPageDigitalDataLayerProps) => {
    const { queryParams: searchParams } = useQueryParams();
    const { searchResult, searchService, sortType } = useSearchContext();
    const { warehouse, deliveryLocation, bdDeliveryLocation } =
        useBrowseContext();
    // TODO: Use this from context over deriving it manually like this.
    const { mdo } = getLocationsFromCookie(warehouse as WarehouseCookieValue);

    const whloc = warehouse?.nearestWarehouse?.catalog;
    const isWAPOEnabledWarehouse = isWAPOEnabledForWarehouse(
        wapoEnabledWarehouses,
        whloc,
        isWAPOEnabled,
    );
    const userLocation = getUserLocationFromDeliveryLocations({
        deliveryLocation,
        bdDeliveryLocation,
        site,
        lang,
    });

    const searchProps = populateFusionAnalyticProps(
        searchParams,
        searchResult,
        searchService,
        sortType,
        grsFacetConfig,
        whloc,
        categoryTitle,
        mdo,
        userLocation,
        isWAPOEnabledWarehouse,
    );

    return (
        <>
            <SearchPageDigitalDataLayer searchProps={searchProps} {...rest} />
            <SearchPageDigitalDataLayer lang={lang} site={site} {...rest} />
        </>
    );
};

export default SearchPageDigitalDataLayer;
